import React from 'react';
import { Modal, Form, Input, Select, TimePicker, Switch, InputNumber, Row, Col, Divider, Typography, Checkbox, Button, Radio, Space } from 'antd';
import { ClockCircleOutlined, UserOutlined, SettingOutlined, CoffeeOutlined, SafetyCertificateOutlined, ApartmentOutlined } from '@ant-design/icons';
import { DepartmentCreateDTO, DepartmentUpdateDTO, DepartmentStatus } from '../../../types/department.types';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PermissionType, User } from '../../../types/user.types';
import { DepartmentDto } from '../../../types/department.types';
import { ApprovalModule } from '../../../types/approval.types';
import ApprovalHierarchyModal from '../../features/department/ApprovalHierarchyModal';

const { Title, Text } = Typography;

const ACTIVITY_APPROVAL_TYPE_OPTIONS = [
  { value: 'VISIT', label: 'Ziyaret' },
  { value: 'DELIVERY', label: 'Teslimat' },
  { value: 'MAINTENANCE', label: 'Bakım' },
  { value: 'OTHER', label: 'Diğer' },
  { value: 'OFFICE', label: 'Ofis' },
  { value: 'VEHICLE', label: 'Araçlı aktiviteler' },
];

interface DepartmentModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: DepartmentCreateDTO | DepartmentUpdateDTO) => void;
  initialValues?: DepartmentCreateDTO | DepartmentUpdateDTO;
  isEdit?: boolean;
  loading?: boolean;
  currentUser: User;
  onOpenHierarchyModal?: (department: DepartmentDto) => void;
}

const DepartmentModal: React.FC<DepartmentModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  isEdit = false,
  loading = false,
  currentUser,
  onOpenHierarchyModal
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const activityApprovalMode = Form.useWatch('activityApprovalMode', form);
  const [approvalHierarchyModule, setApprovalHierarchyModule] = React.useState<ApprovalModule | null>(null);

  React.useEffect(() => {
    if (visible && initialValues) {
      const formValues = {
        ...initialValues,
        fixedShiftStartTime: initialValues.fixedShiftStartTime 
          ? dayjs(initialValues.fixedShiftStartTime, 'HH:mm') 
          : undefined,
        fixedShiftEndTime: initialValues.fixedShiftEndTime 
          ? dayjs(initialValues.fixedShiftEndTime, 'HH:mm') 
          : undefined,
        // String'i array'e çevir
        fixedShiftDays: typeof initialValues.fixedShiftDays === 'string' 
          ? (initialValues.fixedShiftDays as string).split(',').filter((day: string) => day.trim()) 
          : Array.isArray(initialValues.fixedShiftDays) 
            ? initialValues.fixedShiftDays 
            : [],
        fieldModeApprovalMode: initialValues.fieldModeApprovalMode || 'approval',
        activityApprovalMode: initialValues.activityApprovalMode || 'auto',
        activityApprovalTypes: initialValues.activityApprovalTypes || [],
      };
      form.setFieldsValue(formValues);
      setLateTolerance(initialValues.lateTolerance || false);
    } else if (visible) {
      form.resetFields();
      setLateTolerance(false);
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formattedValues = {
        ...values,
        fixedShiftStartTime: values.fixedShiftStartTime?.format('HH:mm'),
        fixedShiftEndTime: values.fixedShiftEndTime?.format('HH:mm'),
        // Array'i string'e çevir
        fixedShiftDays: Array.isArray(values.fixedShiftDays) 
          ? values.fixedShiftDays.join(',') 
          : values.fixedShiftDays || '',
        // Seçili türler sadece "selected" modunda anlamlı
        activityApprovalTypes: values.activityApprovalMode === 'selected' ? values.activityApprovalTypes || [] : [],
      };
      onSubmit(formattedValues);
    } catch (error) {
      // console.error('Form validation error:', error);
    }
  };

  const statusOptions = [
    { value: DepartmentStatus.ACTIVE, label: t('departments.active') },
    { value: DepartmentStatus.INACTIVE, label: t('departments.inactive') },
  ];

  const [lateTolerance, setLateTolerance] = React.useState(false);

  const canManageApprovalHierarchy =
    isEdit && !!(initialValues as DepartmentDto | undefined)?.id && currentUser?.permissions?.includes(PermissionType.EDIT_DEPARTMENTS);

  return (
    <>
    <Modal
      title={
        <div className="d-flex align-items-center">
          <UserOutlined className="me-2" />
          {isEdit ? t('departments.editDepartment') : t('departments.addDepartment')}
        </div>
      }
      open={visible}
      onCancel={currentUser?.permissions?.includes(PermissionType.EDIT_DEPARTMENTS) ? onCancel : undefined}
      onOk={currentUser?.permissions?.includes(PermissionType.EDIT_DEPARTMENTS) ? handleSubmit : undefined}
      confirmLoading={loading}
      width={800}
      okText={isEdit ? t('departments.saveDepartment') : t('departments.addDepartment')}
      cancelText={t('departments.cancelDepartment')}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: DepartmentStatus.ACTIVE,
          lateTolerance: false,
          fieldModeApprovalMode: 'approval',
          activityApprovalMode: 'auto',
          activityApprovalTypes: [],
        }}
      >
        {/* Temel Bilgiler */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <UserOutlined className="me-2" />
            Temel Bilgiler
          </Title>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="name"
                label={t('departments.departmentName')}
                rules={[
                  { required: true, message: t('departments.departmentNameError') },
                  { min: 2, message: t('departments.departmentNameError1') },
                  { max: 50, message: t('departments.departmentNameError2') }
                ]}
              >
                <Input size="large" placeholder="Departman adını giriniz" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="status"
                label={t('departments.status')}
                rules={[{ required: true, message: 'Durum seçiniz' }]}
              >
                <Select size="large" options={statusOptions} />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* Vardiya Ayarları */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <ClockCircleOutlined className="me-2" />
            Çalışma Saatleri
          </Title>
          <Text type="secondary" className="d-block mb-3">
            Departmanın çalışma saatlerini belirleyebilirsiniz
          </Text>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fixedShiftStartTime"
                label="Departman Başlangıç Saati"
                rules={[{ required: true, message: 'Başlangıç saati zorunludur' }]}
              >
                <TimePicker
                  size="large"
                  format="HH:mm"
                  placeholder="Başlangıç Saati"
                  className="w-100"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="fixedShiftEndTime"
                label="Departman Bitiş Saati"
                rules={[{ required: true, message: 'Bitiş saati zorunludur' }]}
              >
                <TimePicker
                  size="large"
                  format="HH:mm"
                  placeholder="Bitiş Saati"
                  className="w-100"
                />
              </Form.Item>
            </Col>
          </Row>
          
          {/* Çalışma Günleri */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="fixedShiftDays"
                label="Çalışma Günleri"
                rules={[{ required: true, message: 'En az bir çalışma günü seçmelisiniz' }]}
              >
                <Checkbox.Group
                  options={[
                    { label: 'Pazartesi', value: 'Monday' },
                    { label: 'Salı', value: 'Tuesday' },
                    { label: 'Çarşamba', value: 'Wednesday' },
                    { label: 'Perşembe', value: 'Thursday' },
                    { label: 'Cuma', value: 'Friday' },
                    { label: 'Cumartesi', value: 'Saturday' },
                    { label: 'Pazar', value: 'Sunday' },
                  ]}
                  className="d-flex flex-wrap gap-2"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* Geç Kalma Ayarları */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <SettingOutlined className="me-2" />
            Geç Kalma Toleransı
          </Title>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="lateTolerance"
                label="Geç Kalma Toleransı"
                valuePropName="checked"
                rules={[{ required: true, message: 'Tolerans durumu seçmelisiniz' }]}
              >
                <div className="d-flex align-items-center">
                  <Switch 
                    onChange={(checked) => {
                      setLateTolerance(checked);
                      form.setFieldsValue({ lateTolerance: checked });
                    }}
                    checked={lateTolerance}
                  />
                </div>
              </Form.Item>
            </Col>
            {lateTolerance && (
              <Col span={12}>
                <Form.Item
                  name="lateTolerationMinutes"
                  label="Tolerans Süresi (Dakika)"
                  rules={[{ required: true, message: 'Tolerans süresi giriniz' }]}
                >
                  <InputNumber
                    size="large"
                    min={1}
                    max={60}
                    placeholder="Dakika"
                    className="w-100"
                    addonAfter="dakika"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </div>

        <Divider />

        {/* Mola Ayarları */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <CoffeeOutlined className="me-2" />
            Mola Ayarları
          </Title>
          <Text type="secondary" className="d-block mb-3">
            Departman için varsayılan mola sürelerini belirleyebilirsiniz
          </Text>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="defaultBreakDuration"
                label="Bir Molanın maksimum süresi"
                rules={[{ required: true, message: 'Varsayılan mola süresi zorunludur' }]}
              >
                <InputNumber
                  size="large"
                  min={5}
                  max={120}
                  placeholder="Dakika"
                  className="w-100"
                  addonAfter="dakika"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxBreakDuration"
                label="Günlük Maksimum Mola Süresi"
                rules={[{ required: true, message: 'Maksimum mola süresi zorunludur' }]}
              >
                <InputNumber
                  size="large"
                  min={5}
                  max={240}
                  placeholder="Dakika"
                  className="w-100"
                  addonAfter="dakika"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="maxMealBreakDuration"
                label="Yemek Molası Limiti (dakika)"
              >
                <InputNumber
                  size="large"
                  min={15}
                  max={180}
                  placeholder="Dakika"
                  className="w-100"
                  addonAfter="dakika"
                />
              </Form.Item>
              <Text type="secondary" style={{ marginTop: '-15px', display: 'block' }}>
                Bu departmana özel bir yemek molası limiti belirleyebilirsiniz. Boş bırakılırsa, genel şirket ayarı kullanılır.
              </Text>
            </Col>
          </Row>
        </div>

        <Divider />

        {/* Fazla Mesai Ayarları */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <ClockCircleOutlined className="me-2" />
            {t('overtime.overtimeManagement', 'Fazla Mesai Yönetimi')}
          </Title>
          <Text type="secondary" className="d-block mb-3">
            {t('overtime.overtimeManagementDescription', 'Departman için fazla mesai talep sistemini aktif edebilirsiniz. Aktif edilirse, çalışanlar fazla mesai talebi oluşturabilir ve yönetici onayı gerekir.')}
          </Text>
          <Form.Item
            name="overtimeRequestEnabled"
            label={t('overtime.manualRequestSystem', 'Manuel Talep Sistemi')}
            valuePropName="checked"
            tooltip={t('overtime.manualRequestSystemTooltip', 'Aktif edilirse çalışanlar fazla mesai talebi oluşturur, yönetici onaylar. Pasif ise sistem otomatik hesaplar.')}
          >
            <Switch 
              checkedChildren={t('overtime.active', 'Aktif')} 
              unCheckedChildren={t('overtime.passive', 'Pasif')}
            />
          </Form.Item>
        </div>

        <Divider />

        {/* Onay Ayarları */}
        <div className="mb-4">
          <Title level={5} className="mb-3">
            <SafetyCertificateOutlined className="me-2" />
            Onay Ayarları
          </Title>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="fieldModeApprovalMode"
                label="Hibrit çalışan saha modu talebi"
                extra="Otomatik onayda bu departmandaki tüm hibrit çalışanların saha/ofis geçişleri onay beklemeden uygulanır."
              >
                <Select
                  size="large"
                  options={[
                    { value: 'auto', label: 'Otomatik onay' },
                    { value: 'approval', label: 'Onay gerekli' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="activityApprovalMode"
                label="Saha aktivitesi onayı"
                extra="Çalışanların kendi oluşturduğu aktiviteler için geçerlidir. Yönetici tarafından atanan aktiviteler doğrudan planlanır."
              >
                <Radio.Group>
                  <Space direction="vertical">
                    <Radio value="auto">Otomatik</Radio>
                    <Radio value="all">Tüm aktiviteler onay ister</Radio>
                    <Radio value="selected">Sadece seçili türler onay ister</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Col>
            {activityApprovalMode === 'selected' && (
              <Col span={24}>
                <Form.Item
                  name="activityApprovalTypes"
                  label="Onay isteyen aktivite türleri"
                  rules={[{ required: true, type: 'array', min: 1, message: 'En az bir aktivite türü seçmelisiniz' }]}
                >
                  <Select
                    size="large"
                    mode="multiple"
                    placeholder="Tür seçiniz"
                    options={ACTIVITY_APPROVAL_TYPE_OPTIONS}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          {canManageApprovalHierarchy && (
            <>
              <Text type="secondary" className="d-block mb-2">
                Hiyerarşi tanımlanmazsa talepler şirket yöneticisi veya departman yöneticisi tarafından tek adımda onaylanır.
              </Text>
              <Space wrap>
                <Button icon={<ApartmentOutlined />} onClick={() => setApprovalHierarchyModule('field_mode')}>
                  Saha Modu Onay Hiyerarşisi
                </Button>
                <Button icon={<ApartmentOutlined />} onClick={() => setApprovalHierarchyModule('field_activity')}>
                  Aktivite Onay Hiyerarşisi
                </Button>
              </Space>
            </>
          )}
        </div>

        {isEdit && initialValues && onOpenHierarchyModal && currentUser?.permissions?.includes(PermissionType.MANAGE_LEAVE_HIERARCHY) && (
          <>
            <Divider />
            <div className="mb-4">
              <Title level={5} className="mb-3">
                <SettingOutlined className="me-2" />
                {t('departments.leaveHierarchy', 'İzin Onay Hiyerarşisi')}
              </Title>
              <Button onClick={() => onOpenHierarchyModal(initialValues as DepartmentDto)}>
                {t('departments.manageHierarchy', 'İzin Hiyerarşisini Yönet')}
              </Button>
            </div>
          </>
        )}
      </Form>
    </Modal>

    {canManageApprovalHierarchy && (
      <ApprovalHierarchyModal
        visible={!!approvalHierarchyModule}
        module={approvalHierarchyModule || 'field_mode'}
        department={{
          id: (initialValues as DepartmentDto).id,
          name: (initialValues as DepartmentDto).name,
        }}
        onClose={() => setApprovalHierarchyModule(null)}
      />
    )}
    </>
  );
};

export default DepartmentModal; 